import { useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';

import { useLayoutScroll } from '~/features/navigation/hooks/useLayoutScroll';
import CloseIcon from '~/icons/close.svg';
import { IVariation } from '~/lib/data-contract';
import { Icon } from '~/shared/components/Icon/Icon';
import { Text } from '~/shared/components/Text/Text';
import { useFrame } from '~/shared/utils/frame/hooks/useFrame';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';

import styles from './productPickerPane.module.css';

import { ProductPickerGrid } from '../ProductPickerGrid/ProductPickerGrid';
import { ProductPickerItem } from '../ProductPickerItem/ProductPickerItem';
import { ProductPickerLabelViewAll } from '../ProductPickerLabel/ProductPickerLabel';
import type { ProductPickerTrackType } from '../ProductPickerTrack/ProductPickerTrack';

export type ProductPickerPaneProps = {
	quantity: number;
	variants: IVariation[];
	title: string;
	type: ProductPickerTrackType;
	onVariationClick: (variation: IVariation) => void;
};

export const ProductPickerPane = ({ quantity, title, variants, type, onVariationClick }: ProductPickerPaneProps) => {
	const [isOpen, setOpen] = useState(false);
	const { translate } = useTranslation();
	const {
		data: { vertical },
	} = useFrame();

	useLayoutScroll(!isOpen);

	const handleOpenChange = (open: boolean) => setOpen(open);

	return (
		<Dialog.Root
			open={isOpen}
			onOpenChange={handleOpenChange}
		>
			<Dialog.Trigger asChild>
				<ProductPickerItem collapsed>
					<ProductPickerLabelViewAll>
						{translate('product-picker.view-all', {
							variants: quantity,
						})}
					</ProductPickerLabelViewAll>
				</ProductPickerItem>
			</Dialog.Trigger>
			<Dialog.Portal>
				<Dialog.Overlay className={`${styles.overlay} theme--${vertical?.brandNo || 'default'}`}>
					<div className={styles.overlayBackdrop} />
					<Dialog.Content className={styles.drawer}>
						<Dialog.Close asChild>
							<button
								className={styles.closeButton}
								aria-label="Close dialog"
							>
								<Icon size="xl">
									<CloseIcon />
								</Icon>
							</button>
						</Dialog.Close>
						<Dialog.Title asChild>
							<Text
								as="h2"
								variant="display3Push"
								marginBottom="6"
							>
								{title}
							</Text>
						</Dialog.Title>
						<ProductPickerGrid
							onVariationClick={onVariationClick}
							variants={variants}
							type={type}
							setOpen={setOpen}
						/>
					</Dialog.Content>
				</Dialog.Overlay>
			</Dialog.Portal>
		</Dialog.Root>
	);
};
